import { useEffect, useState, useRef, useCallback, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Download, Upload, FileSpreadsheet, Trash2, Users, CheckCircle2, Clock, ListChecks, Printer, AlertTriangle, Wand2, TrendingUp, TrendingDown, XCircle, ClipboardList, UserCheck } from "lucide-react";
import { getReferralDashboard, importReferralHistorical, clearReferralHistorical, getDistricts, backfillSendingDistrict, updateReferral, createDistrictAlias, getReferralBackfillCandidates, backfillAcceptReferrals, reclassifyReferral } from "../../lib/api";
import { downloadFromApi } from "../../lib/download";
import { useAuth } from "../../App";
import FormerStudentsSection from "./FormerStudentsSection";

const STATUS_COLORS = {
  accepted: "#16a34a",
  waitlist: "#f59e0b",
  rejected: "#dc2626",
  in_progress: "#2563eb",
};
const STATUS_LABELS = {
  accepted: "Accepted",
  waitlist: "Waitlist",
  rejected: "Rejected",
  in_progress: "In Progress",
};
const STATUS_KEYS = ["accepted", "waitlist", "rejected", "in_progress"];
const SELF_OTHER = "Self / Independent / Other";

const yearLabel = (y) => (y || "").replace("-", "\u2013"); // en-dash for display
const truncate = (s, n = 18) => (s && s.length > n ? `${s.slice(0, n)}\u2026` : s || "");

// Wrap a long district name onto up to 2 lines (max ~28 chars/line) so full
// names stay readable on the vertical bar chart's Y axis instead of being clipped.
const wrapLabel = (s, maxChars = 28, maxLines = 2) => {
  const words = String(s || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    if (!line) { line = w; }
    else if ((line + " " + w).length <= maxChars) { line += " " + w; }
    else { lines.push(line); line = w; }
    if (lines.length === maxLines - 1 && line.length > maxChars) break;
  }
  if (line) lines.push(line);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = truncate(kept[maxLines - 1], maxChars - 1);
    return kept;
  }
  return lines;
};

const DistrictTick = ({ x, y, payload }) => {
  const lines = wrapLabel(payload?.value);
  const dy = -((lines.length - 1) * 6);
  return (
    <text x={x} y={y} dy={dy} textAnchor="end" fontSize={12} fill="#334155">
      {lines.map((ln, i) => (
        <tspan key={i} x={x} dx={-6} dy={i === 0 ? 4 : 13}>{ln}</tspan>
      ))}
    </text>
  );
};

// Distinct colors per academic year for the year-over-year trend lines / bars.
const YEAR_PALETTE = ["#00305E", "#16a34a", "#f59e0b", "#8b5cf6", "#dc2626", "#0891b2", "#db2777", "#65a30d", "#2563eb", "#475569"];

// Multi-line year-over-year monthly chart (one line per academic year, months Jul→Jun).
function MonthlyTrendChart({ testid, title, description, icon: Icon, tint, series, emptyText }) {
  const years = series?.years || [];
  const data = series?.data || [];
  const hasData = years.length > 0 && data.some((row) => years.some((y) => (row[y] || 0) > 0));
  return (
    <Card data-testid={testid}>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          {Icon && (
            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${tint}`}>
              <Icon className="h-4 w-4" />
            </span>
          )}
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid={`${testid}-empty`}>{emptyText}</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={data} margin={{ left: 8, right: 24, top: 12, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" fontSize={12} />
              <YAxis allowDecimals={false} fontSize={11} />
              <Tooltip />
              <Legend />
              {years.map((y, i) => (
                <Line
                  key={y}
                  type="monotone"
                  dataKey={y}
                  name={yearLabel(y)}
                  stroke={YEAR_PALETTE[i % YEAR_PALETTE.length]}
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

// Referral outcome reasons — one horizontal bar per reason (count), sorted by
// count, matching the Former Students "By Exit Reason" breakdown style.
function OutcomeReasonsChart({ trends }) {
  const rows = (trends?.data || [])
    .map((r) => ({ reason: r.reason, count: r.total || 0 }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
  return (
    <Card data-testid="outcome-reasons-chart">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-rose-50 text-rose-600">
            <XCircle className="h-4 w-4" />
          </span>
          Referral Outcome Reasons
        </CardTitle>
        <CardDescription>Why referred students didn&apos;t enroll — split by reason</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="outcome-reasons-empty">
            No outcome reasons recorded yet. Set a Referral Outcome Reason on waitlisted or declined referrals to see the breakdown here.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, rows.length * 42)}>
            <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 36, top: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} fontSize={11} />
              <YAxis type="category" dataKey="reason" width={220} fontSize={12} interval={0} />
              <Tooltip labelFormatter={(v) => v} />
              <Bar dataKey="count" name="Referrals" fill="#e11d48" radius={[0, 4, 4, 0]}>
                <LabelList dataKey="count" position="right" fontSize={11} fill="#475569" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default function ReferralTrendsDashboard() {
  const navigate = useNavigate();
  const { isHQViewer } = useAuth();
  const readOnly = isHQViewer(); // HQ Viewer sees the dashboard read-only (writes 403 server-side)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [drillDistrict, setDrillDistrict] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [matching, setMatching] = useState(false);
  const [groupSource, setGroupSource] = useState(null);
  const [groupTarget, setGroupTarget] = useState("");
  const [auditFix, setAuditFix] = useState(null);
  const [auditForm, setAuditForm] = useState({});
  const [auditSaving, setAuditSaving] = useState(false);
  const [backfill, setBackfill] = useState([]);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [rowDistrict, setRowDistrict] = useState({});
  const [reclassify, setReclassify] = useState(null);
  const [reclassForm, setReclassForm] = useState({ sending_district: "", status: "" });
  const [reclassSaving, setReclassSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getReferralDashboard();
      setData(res.data);
    } catch {
      toast.error("Failed to load the Referral Trends dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBackfill = useCallback(async () => {
    if (readOnly) return;
    try {
      const res = await getReferralBackfillCandidates();
      setBackfill(res.data?.candidates || []);
    } catch {
      /* non-blocking: the dashboard still renders without the backfill card */
    }
  }, [readOnly]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadBackfill(); }, [loadBackfill]);

  useEffect(() => {
    getDistricts().then((r) => {
      const list = (r.data || []).filter((d) => d?.name).sort((a, b) => a.name.localeCompare(b.name));
      setDistricts(list);
    }).catch(() => {});
  }, []);

  const handleAutoMatch = async () => {
    setMatching(true);
    try {
      const res = await backfillSendingDistrict();
      const { matched, flagged } = res.data;
      toast.success(`Auto-match complete: ${matched} matched, ${flagged} flagged for review`);
      load();
    } catch {
      toast.error("Auto-match failed");
    } finally {
      setMatching(false);
    }
  };

  const assignDistrict = async (id, name) => {
    try {
      await updateReferral(id, { sending_district: name, sending_district_needs_review: false });
      toast.success("Sending district assigned");
      load();
    } catch {
      toast.error("Could not assign district");
    }
  };

  const openAuditFix = (r) => {
    setAuditFix(r);
    setAuditForm({
      referral_date: r.referral_date || "",
      waitlist_date: r.waitlist_date || "",
      declined_date: r.declined_date || "",
      sending_district: r.sending_district || "",
    });
  };

  const saveAuditFix = async () => {
    if (!auditFix) return;
    setAuditSaving(true);
    try {
      const payload = {};
      ["referral_date", "waitlist_date", "declined_date", "sending_district"].forEach((k) => {
        if (auditForm[k]) payload[k] = auditForm[k];
      });
      if (auditForm.sending_district) payload.sending_district_needs_review = false;
      await updateReferral(auditFix.id, payload);
      toast.success("Referral details updated");
      setAuditFix(null);
      load();
    } catch {
      toast.error("Could not save referral details");
    } finally {
      setAuditSaving(false);
    }
  };

  const runBackfill = async (ids) => {
    if (!ids.length) return;
    setBackfillBusy(true);
    try {
      const res = await backfillAcceptReferrals(ids);
      toast.success(`Marked ${res.data?.updated ?? ids.length} student${(res.data?.updated ?? ids.length) === 1 ? "" : "s"} as Accepted`);
      await Promise.all([load(), loadBackfill()]);
    } catch {
      toast.error("Could not backfill referral status");
    } finally {
      setBackfillBusy(false);
    }
  };

  // Mark one candidate Accepted — assigning the chosen Sending District first
  // (when they don't already have one) so they land under the right district.
  const markRowAccepted = async (c) => {
    const chosen = rowDistrict[c.id];
    setBackfillBusy(true);
    try {
      if (!c.sending_district && chosen) {
        await updateReferral(c.id, { sending_district: chosen, sending_district_needs_review: false });
      }
      await backfillAcceptReferrals([c.id]);
      toast.success(`Marked ${c.name} as Accepted${(!c.sending_district && chosen) ? ` · ${chosen}` : ""}`);
      setRowDistrict((prev) => { const n = { ...prev }; delete n[c.id]; return n; });
      await Promise.all([load(), loadBackfill()]);
    } catch {
      toast.error("Could not mark student Accepted");
    } finally {
      setBackfillBusy(false);
    }
  };

  const confirmGroup = async () => {
    if (!groupSource || !groupTarget) return;
    try {
      await createDistrictAlias({ alias: groupSource, canonical: groupTarget });
      toast.success(`"${groupSource}" now grouped under "${groupTarget}"`);
      setGroupSource(null);
      setGroupTarget("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not group district");
    }
  };

  // Per-student manual reclassify — directly set the correct Sending District
  // and/or Status on one record (live student or historical row), bypassing the
  // district canonicalizer so a mis-grouped student is fixed in one click.
  const openReclassify = (r) => {
    setReclassify(r);
    setReclassForm({
      sending_district: r.raw_district || (r.district !== SELF_OTHER ? r.district : "") || "",
      status: r.status || "",
    });
  };

  const saveReclassify = async () => {
    if (!reclassify) return;
    const r = reclassify;
    const payload = { source: r.source };
    if (r.source === "historical") payload.hid = r.hid;
    else payload.student_id = r.student_id;
    const newDistrict = (reclassForm.sending_district || "").trim();
    const curDistrict = r.raw_district || (r.district !== SELF_OTHER ? r.district : "") || "";
    if (newDistrict && newDistrict !== curDistrict) payload.sending_district = newDistrict;
    if (reclassForm.status && reclassForm.status !== r.status) payload.status = reclassForm.status;
    if (!payload.sending_district && !payload.status) {
      toast.info("No changes to save");
      return;
    }
    setReclassSaving(true);
    try {
      const res = await reclassifyReferral(payload);
      toast.success(`${r.name} reclassified`);
      if (res.data?.note) toast.info(res.data.note, { duration: 6000 });
      setReclassify(null);
      setDrillDistrict(null);
      await Promise.all([load(), loadBackfill()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reclassify this student");
    } finally {
      setReclassSaving(false);
    }
  };

  const handlePrint = () => {
    document.body.classList.add("print-dashboard");
    const cleanup = () => {
      document.body.classList.remove("print-dashboard");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    setTimeout(() => window.print(), 150);
  };

  const trendData = useMemo(() => {
    const by = data?.by_year || [];
    return by.map((y) => {
      const total = (y.accepted || 0) + (y.waitlist || 0) + (y.rejected || 0) + (y.in_progress || 0);
      return { year: y.year, rate: total ? Math.round((y.accepted / total) * 1000) / 10 : 0, total };
    });
  }, [data]);

  const drillRecords = useMemo(() => {
    if (!drillDistrict || !data?.records) return [];
    const order = { accepted: 0, waitlist: 1, rejected: 2, in_progress: 3 };
    return data.records
      .filter((r) => r.district === drillDistrict)
      .sort((a, b) =>
        (a.academic_year || "").localeCompare(b.academic_year || "") ||
        (order[a.status] - order[b.status]) ||
        (a.name || "").localeCompare(b.name || "")
      );
  }, [drillDistrict, data]);

  const handleTemplate = async () => {
    try {
      await downloadFromApi("/api/referrals/historical/template", "referral_historical_template.csv");
    } catch {
      toast.error("Could not download the template");
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadFromApi("/api/referrals/export", "referral_data_export.csv");
    } catch {
      toast.error("Could not export referral data");
    } finally {
      setExporting(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await importReferralHistorical(fd);
      const { imported, updated = 0, skipped, errors } = res.data;
      toast.success(
        `Imported ${imported} new` +
        (updated ? ` · ${updated} updated` : "") +
        (skipped ? ` · ${skipped} skipped` : "")
      );
      if (errors?.length) {
        toast.error(errors.slice(0, 3).join(" "), { duration: 8000 });
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed. Check the file format.");
    } finally {
      setUploading(false);
    }
  };

  const handleClear = async () => {
    try {
      const res = await clearReferralHistorical();
      toast.success(`Cleared ${res.data.deleted} historical record${res.data.deleted === 1 ? "" : "s"}`);
      load();
    } catch {
      toast.error("Could not clear historical data");
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-muted-foreground" data-testid="referral-dashboard-loading">Loading trends…</div>;
  }
  if (!data) return null;

  const { summary, academic_years: years, rows, totals, by_district_status, by_year, historical_count, needs_review = [], monthly_intake, monthly_disenrollment, outcome_trends, audit_flags = [] } = data;
  const isEmpty = (rows || []).length === 0;

  return (
    <div className="space-y-6" data-testid="referral-trends-dashboard">
      {/* Print / export toolbar (hidden in the printout) */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Referral Trends &amp; District Summary</h2>
          <p className="text-xs text-muted-foreground">Grouped by Sending District across academic years</p>
        </div>
        <div className="flex items-center gap-2">
          {!readOnly && (
            <Button variant="outline" onClick={handleAutoMatch} disabled={matching} data-testid="auto-match-btn">
              <Wand2 className="h-4 w-4 mr-2" /> {matching ? "Matching…" : "Auto-Match Districts"}
            </Button>
          )}
          <Button variant="outline" onClick={handlePrint} data-testid="print-dashboard-btn">
            <Printer className="h-4 w-4 mr-2" /> Print / PDF
          </Button>
        </div>
      </div>

      {/* Needs-review banner: referrals whose sending district couldn't be matched */}
      {needs_review.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60 print:hidden" data-testid="needs-review-banner">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-4 w-4" /> {needs_review.length} referral{needs_review.length === 1 ? "" : "s"} {needs_review.length === 1 ? "needs" : "need"} a Sending District
            </CardTitle>
            <CardDescription>These were referred by free text that couldn&apos;t be confidently matched. Pick the correct district to include them in the totals.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {needs_review.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 py-1.5 border-b border-amber-200 last:border-0" data-testid={`needs-review-row-${r.id}`}>
                <span className="font-medium text-sm min-w-[140px]">{r.name}</span>
                <span className="text-xs text-muted-foreground flex-1">Referred from: <span className="italic">{r.referred_from || "—"}</span></span>
                <Select value={undefined} onValueChange={(v) => assignDistrict(r.id, v)}>
                  <SelectTrigger className="w-64 h-8 bg-white" data-testid={`assign-district-${r.id}`}>
                    <SelectValue placeholder={r.suggestion ? `Suggested: ${r.suggestion}` : "Assign district…"} />
                  </SelectTrigger>
                  <SelectContent>
                    {districts.map((d) => (
                      <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Data-quality audit: referrals missing a referral date, waitlist/rejection date, or Sending District */}
      {!readOnly && backfill.length > 0 && (
        <Card className="border-sky-300 bg-sky-50/60 print:hidden" data-testid="referral-backfill-banner">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-sky-800">
              <UserCheck className="h-4 w-4" /> {backfill.length} enrolled student{backfill.length === 1 ? "" : "s"} came through the referral pipeline but {backfill.length === 1 ? "isn't" : "aren't"} on this dashboard
            </CardTitle>
            <CardDescription>
              These currently-enrolled students show signs of a referral (referral date, referrer, pipeline steps, a sending district, or a historical row) but their referral status was never set to Accepted, so they aren&apos;t counted here. Review and mark them Accepted — this only updates their referral status, no other student-file data changes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-end">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700" disabled={backfillBusy} data-testid="backfill-all-btn">
                    <UserCheck className="h-3.5 w-3.5 mr-1.5" /> Mark all {backfill.length} as Accepted
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Mark {backfill.length} student{backfill.length === 1 ? "" : "s"} as Accepted?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This sets each listed student&apos;s referral status to Accepted so they appear on the Referral Trends dashboard under their sending district. No enrollment or other student-file data is changed.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => runBackfill(backfill.map((c) => c.id))} data-testid="backfill-all-confirm">
                      Mark all Accepted
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            {backfill.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-3 py-1.5 border-b border-sky-200 last:border-0" data-testid={`backfill-row-${c.id}`}>
                <span className="font-medium text-sm min-w-[150px]">
                  {c.name}{c.grade ? <span className="text-slate-500 font-normal"> · Gr {c.grade}</span> : null}
                </span>
                <span className="flex-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                  {c.sending_district
                    ? <span className="font-medium px-2 py-0.5 rounded-full bg-sky-200/70 text-sky-800">{c.sending_district}</span>
                    : (
                      <Select value={rowDistrict[c.id] || ""} onValueChange={(v) => setRowDistrict((prev) => ({ ...prev, [c.id]: v }))}>
                        <SelectTrigger className="h-8 w-[240px] bg-white text-xs" data-testid={`backfill-district-${c.id}`}>
                          <SelectValue placeholder="Assign sending district (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {districts.map((d) => (
                            <SelectItem key={d.id || d.name} value={d.name}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  {c.referral_date ? <span className="text-slate-500">Referred {c.referral_date}</span> : null}
                  {c.referred_from ? <span className="text-slate-500">from {c.referred_from}</span> : null}
                </span>
                <Button size="sm" variant="outline" className="h-8 bg-white" onClick={() => markRowAccepted(c)} disabled={backfillBusy} data-testid={`backfill-one-${c.id}`}>
                  {(!c.sending_district && rowDistrict[c.id]) ? "Assign & Accept" : "Mark Accepted"}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {audit_flags.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60 print:hidden" data-testid="referral-audit-banner">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <ClipboardList className="h-4 w-4" /> {audit_flags.length} referral{audit_flags.length === 1 ? "" : "s"} {audit_flags.length === 1 ? "is" : "are"} missing information
            </CardTitle>
            <CardDescription>These referral records are missing a referral date, a waitlist/rejection date, or a Sending District. Click in to fill in the missing details.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {audit_flags.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 py-1.5 border-b border-amber-200 last:border-0" data-testid={`referral-audit-row-${r.id}`}>
                <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /></span>
                <span className="font-medium text-sm min-w-[140px]">{r.name}</span>
                <span className="flex-1 flex flex-wrap gap-1.5">
                  {r.missing.map((m) => (
                    <span key={m} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-200/70 text-amber-800">{m}</span>
                  ))}
                </span>
                <Button size="sm" variant="outline" className="h-8 bg-white" onClick={() => openAuditFix(r)} data-testid={`fix-referral-${r.id}`} disabled={readOnly}>
                  Fill in details
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div id="referral-trends-print" className="space-y-6">
      {/* Print-only heading */}
      <div className="hidden print:block mb-2">
        <h1 className="text-xl font-bold text-[#00305E]">Referral Trends &amp; District Summary Dashboard</h1>
        <p className="text-xs text-slate-500">Generated {new Date().toLocaleDateString()}</p>
      </div>

      {/* Summary stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-total" icon={Users} tint="slate" label="Total Referrals" value={summary.total} sub="All tracked referrals" />
        <StatCard testid="stat-acceptance" icon={CheckCircle2} tint="emerald" label="Acceptance Rate" value={`${summary.acceptance_rate}%`} sub="Admissions conversion" />
        <StatCard testid="stat-waitlist" icon={ListChecks} tint="amber" label="Waitlist Count" value={summary.waitlist_count} sub="Awaiting placement" />
        <StatCard testid="stat-pipeline" icon={Clock} tint="blue" label="Active Pipeline" value={summary.active_pipeline} sub="Currently in review" />
      </div>

      {/* District x Year breakdown table */}
      <Card>
        <CardHeader>
          <CardTitle>District Summary by Academic Year</CardTitle>
          <CardDescription>Accepted · Waitlist · Rejected · In Progress per year, with overall totals — click a district row to see the referrals behind the numbers</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isEmpty ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No referral data yet. Add referrals or import historical data below.</p>
          ) : (
            <Table data-testid="district-summary-table">
              <TableHeader>
                <TableRow>
                  <TableHead rowSpan={2} className="align-bottom border-r">Sending District</TableHead>
                  {years.map((y) => (
                    <TableHead key={y} colSpan={5} className="text-center border-r bg-slate-50 font-semibold text-slate-700">
                      {yearLabel(y)}
                    </TableHead>
                  ))}
                  <TableHead colSpan={2} className="text-center bg-[#00305E]/5 font-semibold text-[#00305E]">Overall</TableHead>
                </TableRow>
                <TableRow>
                  {years.map((y) => (
                    <Fragment key={y}>
                      <TableHead className="text-center text-xs">Acc</TableHead>
                      <TableHead className="text-center text-xs">Wait</TableHead>
                      <TableHead className="text-center text-xs">Rej</TableHead>
                      <TableHead className="text-center text-xs">In Prog</TableHead>
                      <TableHead className="text-center text-xs font-semibold border-r">Total</TableHead>
                    </Fragment>
                  ))}
                  <TableHead className="text-center text-xs font-semibold">Grand Total</TableHead>
                  <TableHead className="text-center text-xs font-semibold">Accept Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.district}
                    data-testid={`district-row-${row.district}`}
                    className="cursor-pointer hover:bg-slate-50 print:cursor-auto"
                    onClick={() => setDrillDistrict(row.district)}
                  >
                    <TableCell className="font-medium whitespace-nowrap border-r text-blue-700 hover:underline print:text-slate-800 print:no-underline">
                      <span className="inline-flex items-center gap-2">
                        <span onClick={() => setDrillDistrict(row.district)}>{row.district}</span>
                        {row.district !== SELF_OTHER && !readOnly && (
                          <button
                            type="button"
                            title="Group this district under another (merge duplicates)"
                            className="print:hidden text-[10px] font-normal text-slate-400 hover:text-[#00305E] border border-slate-200 hover:border-[#00305E] rounded px-1.5 py-0.5"
                            data-testid={`group-district-btn-${row.district}`}
                            onClick={(e) => { e.stopPropagation(); setGroupSource(row.district); setGroupTarget(""); }}
                          >
                            Group
                          </button>
                        )}
                      </span>
                    </TableCell>
                    {years.map((y) => {
                      const c = row.per_year[y] || {};
                      return (
                        <Fragment key={y}>
                          <TableCell className="text-center">{c.accepted || 0}</TableCell>
                          <TableCell className="text-center">{c.waitlist || 0}</TableCell>
                          <TableCell className="text-center">{c.rejected || 0}</TableCell>
                          <TableCell className="text-center">{c.in_progress || 0}</TableCell>
                          <TableCell className="text-center font-semibold border-r">{c.total || 0}</TableCell>
                        </Fragment>
                      );
                    })}
                    <TableCell className="text-center font-semibold">{row.grand_total}</TableCell>
                    <TableCell className="text-center font-semibold">{row.acceptance_rate}%</TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow className="bg-[#00305E]/5 font-bold" data-testid="district-totals-row">
                  <TableCell className="border-r">Total / All Districts</TableCell>
                  {years.map((y) => {
                    const c = totals.per_year[y] || {};
                    return (
                      <Fragment key={y}>
                        <TableCell className="text-center">{c.accepted || 0}</TableCell>
                        <TableCell className="text-center">{c.waitlist || 0}</TableCell>
                        <TableCell className="text-center">{c.rejected || 0}</TableCell>
                        <TableCell className="text-center">{c.in_progress || 0}</TableCell>
                        <TableCell className="text-center border-r">{c.total || 0}</TableCell>
                      </Fragment>
                    );
                  })}
                  <TableCell className="text-center">{totals.grand_total}</TableCell>
                  <TableCell className="text-center">{totals.acceptance_rate}%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Charts */}
      {!isEmpty && (
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Total Referrals by School District &amp; Status</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(320, by_district_status.length * 38)}>
                <BarChart data={by_district_status} layout="vertical" margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} fontSize={11} />
                  <YAxis type="category" dataKey="district" width={220} fontSize={12} interval={0} tick={<DistrictTick />} />
                  <Tooltip />
                  <Legend />
                  {STATUS_KEYS.map((k) => (
                    <Bar key={k} dataKey={k} name={STATUS_LABELS[k]} stackId="a" fill={STATUS_COLORS[k]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Referral Intake Volume by Academic Year</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={by_year} margin={{ left: 8, right: 16, top: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="year" tickFormatter={yearLabel} fontSize={12} />
                  <YAxis allowDecimals={false} fontSize={11} />
                  <Tooltip labelFormatter={yearLabel} />
                  <Legend />
                  {STATUS_KEYS.map((k) => (
                    <Bar key={k} dataKey={k} name={STATUS_LABELS[k]} stackId="a" fill={STATUS_COLORS[k]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Conversion trend line chart */}
      {!isEmpty && trendData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Acceptance Rate Trend by Academic Year</CardTitle>
            <CardDescription>Accepted ÷ total referrals for each academic year</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData} margin={{ left: 8, right: 24, top: 12, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="year" tickFormatter={yearLabel} fontSize={12} />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} fontSize={11} />
                <Tooltip labelFormatter={yearLabel} formatter={(v) => [`${v}%`, "Acceptance Rate"]} />
                <Line type="monotone" dataKey="rate" name="Acceptance Rate" stroke="#16a34a" strokeWidth={3} dot={{ r: 5, fill: "#16a34a" }} activeDot={{ r: 7 }}>
                  <LabelList dataKey="rate" position="top" formatter={(v) => `${v}%`} fontSize={11} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Monthly Intake Trends (year over year) */}
      <MonthlyTrendChart
        testid="monthly-intake-trends"
        title="Monthly Intake Trends"
        description="Referrals received each month, compared year over year (school year runs Jul→Jun)"
        icon={TrendingUp}
        tint="bg-blue-50 text-blue-600"
        series={monthly_intake}
        emptyText="No dated referrals yet. Referrals with a referral date will appear here."
      />

      {/* Monthly Disenrollment Trends (year over year) */}
      <MonthlyTrendChart
        testid="monthly-disenrollment-trends"
        title="Monthly Disenrollment Trends"
        description="Students disenrolled each month, compared year over year (school year runs Jul→Jun)"
        icon={TrendingDown}
        tint="bg-rose-50 text-rose-600"
        series={monthly_disenrollment}
        emptyText="No disenrolled students on record yet."
      />

      {/* Referral Outcome Reasons trend */}
      <OutcomeReasonsChart trends={outcome_trends} />

      {/* Former Students: Graduates vs Disenrolled */}
      <FormerStudentsSection readOnly={readOnly} />
      </div>{/* end #referral-trends-print */}

      {/* Historical CSV import */}
      <Card data-testid="historical-import-card" className="print:hidden">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" /> Historical Data Import &amp; Export</CardTitle>
          <CardDescription>
            Import prior-year referral records from a CSV. Columns: <strong>Student Name, Grade, School District, Academic Year, Status</strong> (Accepted, Waitlist, Rejected, In Progress),
            plus <strong>Referral Date, Waitlist Date, Rejection Date, Outcome Reason, Outcome Notes</strong>. A Referral Date feeds Monthly Intake Trends and an Outcome Reason feeds the Outcome Reasons chart.
            Imported rows are labeled <strong>Historical Data</strong> and included in all totals and charts above.
            Use <strong>Download Referral Data</strong> to export every referral with a <strong>Missing Info</strong> column so you can spot and fill records that aren&apos;t fully tracked yet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={handleTemplate} data-testid="download-template-btn">
              <Download className="h-4 w-4 mr-2" /> Download CSV Template
            </Button>
            <Button variant="outline" onClick={handleExport} disabled={exporting} data-testid="export-referrals-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> {exporting ? "Preparing…" : "Download Referral Data"}
            </Button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} data-testid="historical-file-input" />
            {!readOnly && (
              <Button className="bg-[#00305E] hover:bg-[#004488]" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="upload-historical-btn">
                <Upload className="h-4 w-4 mr-2" /> {uploading ? "Importing…" : "Upload Historical CSV"}
              </Button>
            )}
            <span className="text-sm text-muted-foreground" data-testid="historical-count">
              {historical_count} historical record{historical_count === 1 ? "" : "s"} on file
            </span>
            {historical_count > 0 && !readOnly && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto" data-testid="clear-historical-btn">
                    <Trash2 className="h-4 w-4 mr-1" /> Clear all historical
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear all historical data?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently deletes all {historical_count} imported historical referral records. Live referrals and student files are not affected.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleClear} data-testid="confirm-clear-historical">Clear all</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Group / merge districts dialog */}
      <Dialog open={!!groupSource} onOpenChange={(o) => { if (!o) { setGroupSource(null); setGroupTarget(""); } }}>
        <DialogContent data-testid="group-district-dialog">
          <DialogHeader>
            <DialogTitle>Group district</DialogTitle>
            <DialogDescription>
              Count <strong>{groupSource}</strong> together with another district on this dashboard. This creates a
              grouping alias — it never changes any student or referral record, and you can undo it in
              Contacts ▸ Sending School Set Up.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Group &quot;{groupSource}&quot; under</label>
            <Select value={groupTarget} onValueChange={setGroupTarget}>
              <SelectTrigger data-testid="group-target-select"><SelectValue placeholder="Select the district to keep…" /></SelectTrigger>
              <SelectContent>
                {rows
                  .map((r) => r.district)
                  .filter((d) => d && d !== groupSource && d !== SELF_OTHER)
                  .map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setGroupSource(null); setGroupTarget(""); }}>Cancel</Button>
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={confirmGroup} disabled={!groupTarget} data-testid="confirm-group-btn">Group</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fill-in-missing-details dialog (data-quality audit) */}
      <Dialog open={!!auditFix} onOpenChange={(o) => { if (!o) setAuditFix(null); }}>
        <DialogContent data-testid="referral-audit-dialog">
          <DialogHeader>
            <DialogTitle>Fill in details — {auditFix?.name}</DialogTitle>
            <DialogDescription>
              Add the missing referral information so this record is counted correctly on the dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1">
              <Label className="text-xs">Referral Date</Label>
              <Input
                type="date"
                value={auditForm.referral_date || ""}
                onChange={(e) => setAuditForm((p) => ({ ...p, referral_date: e.target.value }))}
                data-testid="audit-referral-date"
              />
            </div>
            {auditFix?.referral_status === "waitlist" && (
              <div className="space-y-1">
                <Label className="text-xs">Waitlist Date</Label>
                <Input
                  type="date"
                  value={auditForm.waitlist_date || ""}
                  onChange={(e) => setAuditForm((p) => ({ ...p, waitlist_date: e.target.value }))}
                  data-testid="audit-waitlist-date"
                />
              </div>
            )}
            {auditFix?.referral_status === "declined" && (
              <div className="space-y-1">
                <Label className="text-xs">Rejection Date</Label>
                <Input
                  type="date"
                  value={auditForm.declined_date || ""}
                  onChange={(e) => setAuditForm((p) => ({ ...p, declined_date: e.target.value }))}
                  data-testid="audit-declined-date"
                />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Sending District</Label>
              <Select value={auditForm.sending_district || undefined} onValueChange={(v) => setAuditForm((p) => ({ ...p, sending_district: v }))}>
                <SelectTrigger data-testid="audit-sending-district"><SelectValue placeholder="Select a sending district…" /></SelectTrigger>
                <SelectContent>
                  {districts.map((d) => (
                    <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {auditFix?.referred_from ? (
                <p className="text-[11px] text-muted-foreground">Referred from: <span className="italic">{auditFix.referred_from}</span></p>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditFix(null)}>Cancel</Button>
            <Button onClick={saveAuditFix} disabled={auditSaving} data-testid="audit-save-btn">{auditSaving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* District drill-down dialog */}
      <Dialog open={!!drillDistrict} onOpenChange={(o) => !o && setDrillDistrict(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle data-testid="drill-title">{drillDistrict}</DialogTitle>
            <DialogDescription>{drillRecords.length} referral{drillRecords.length === 1 ? "" : "s"} on record for this district</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {drillRecords.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No referrals found.</p>
            ) : (
              <Table data-testid="drill-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Academic Year</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Source</TableHead>
                    {!readOnly && <TableHead className="text-right">Reclassify</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillRecords.map((r, i) => (
                    <TableRow key={`${r.student_id || r.hid || r.name}-${i}`} data-testid={`drill-row-${i}`}>
                      <TableCell className="font-medium">
                        {r.student_id ? (
                          <button className="text-blue-700 hover:underline" onClick={() => navigate(`/students/${r.student_id}`)}>
                            {r.name}
                          </button>
                        ) : r.name}
                      </TableCell>
                      <TableCell>{yearLabel(r.academic_year)}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[r.status] }} />
                          {STATUS_LABELS[r.status]}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded ${r.source === "historical" ? "bg-slate-100 text-slate-600" : "bg-emerald-50 text-emerald-700"}`}>
                          {r.source === "historical" ? "Historical" : "Live"}
                        </span>
                      </TableCell>
                      {!readOnly && (
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => openReclassify(r)}
                            data-testid={`reclassify-btn-${i}`}
                          >
                            Reclassify
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Per-student Reclassify dialog — directly set the correct Sending District
          and/or Status, overriding automatic grouping */}
      <Dialog open={!!reclassify} onOpenChange={(o) => { if (!o) setReclassify(null); }}>
        <DialogContent data-testid="reclassify-dialog">
          <DialogHeader>
            <DialogTitle>Reclassify — {reclassify?.name}</DialogTitle>
            <DialogDescription>
              Move this {reclassify?.source === "historical" ? "historical record" : "student"} to the correct Sending District and/or Status. This updates the record directly and overrides automatic grouping.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1">
              <Label className="text-xs">Sending District</Label>
              <Select
                value={reclassForm.sending_district || undefined}
                onValueChange={(v) => setReclassForm((p) => ({ ...p, sending_district: v }))}
              >
                <SelectTrigger data-testid="reclassify-district-select"><SelectValue placeholder="Select a sending district…" /></SelectTrigger>
                <SelectContent>
                  {districts.map((d) => (
                    <SelectItem key={d.id || d.name} value={d.name}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {reclassify?.raw_district ? (
                <p className="text-[11px] text-muted-foreground">Currently filed as: <span className="italic">{reclassify.raw_district}</span></p>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select
                value={reclassForm.status || undefined}
                onValueChange={(v) => setReclassForm((p) => ({ ...p, status: v }))}
              >
                <SelectTrigger data-testid="reclassify-status-select"><SelectValue placeholder="Select a status…" /></SelectTrigger>
                <SelectContent>
                  {STATUS_KEYS.map((k) => (
                    <SelectItem key={k} value={k}>{STATUS_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {reclassify?.source === "live" && (
                <p className="text-[11px] text-muted-foreground">Enrolled students always count as Accepted — a status change won&apos;t apply to them.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReclassify(null)}>Cancel</Button>
            <Button onClick={saveReclassify} disabled={reclassSaving} data-testid="reclassify-save-btn">
              {reclassSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TINTS = {
  slate: "bg-slate-50 text-slate-600",
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  blue: "bg-blue-50 text-blue-600",
};

function StatCard({ icon: Icon, tint, label, value, sub, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
          <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${TINTS[tint]}`}>
            <Icon className="h-4 w-4" />
          </span>
        </div>
        <p className="mt-2 text-3xl font-bold text-slate-800" data-testid={`${testid}-value`}>{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{sub}</p>
      </CardContent>
    </Card>
  );
}
